import { supabase } from '../supabase';
import { UserGame } from '../../types';
import { processAction } from '../gamification/gamificationService';
import { XP_ACTIONS } from '../gamification/xpConfig';

/**
 * Serviço para gerenciar operações relacionadas ao perfil do usuário
 */
export class UserProfileService {
  // Sistema de cache para reduzir consultas ao banco de dados
  private profileCache: Map<string, {profile: any, timestamp: number}> = new Map();
  private readonly CACHE_TTL = 30 * 60 * 1000; // 30 minutos em milissegundos

  /**
   * Extrai o perfil de preferências do usuário baseado na sua coleção
   */
  public async extractUserProfile(
    userId: string,
    userCollection: UserGame[] = []
  ): Promise<{
    favoriteCategories: string[];
    favoriteMechanics: string[];
    favoriteThemes: string[];
    preferredPlayerCount?: number;
    maxPlayTime?: number;
    preferredComplexity?: number;
  }> {
    try {
      // Verificar cache antes de consultar o banco de dados
      const cached = this.getFromCache(userId);
      let profile = null;
      let profileError = null;

      if (cached) {
        console.log('Perfil recuperado do cache');
        profile = cached;
      } else {
        // Obter perfil do usuário do banco de dados
        const { data, error } = await supabase
          .from('profiles')
          .select('favorite_categories, preferred_player_count, max_play_time, complexity_preference')
          .eq('id', userId)
          .single();
        
        profile = data;
        profileError = error;
        
        if (profileError) {
          console.warn('Erro ao buscar perfil do usuário:', profileError);
        } else {
          console.log('Perfil recuperado do banco de dados com sucesso');
          // Salvar no cache
          this.saveToCache(userId, profile);
        }
      }
      
      // Inicializar com valores padrão
      const userProfile: {
        favoriteCategories: string[];
        favoriteMechanics: string[];
        favoriteThemes: string[];
        preferredPlayerCount?: number;
        maxPlayTime?: number;
        preferredComplexity?: number;
      } = {
        favoriteCategories: [],
        favoriteMechanics: [],
        favoriteThemes: [],
        preferredPlayerCount: undefined,
        maxPlayTime: undefined,
        preferredComplexity: undefined
      };
      
      // Adicionar quaisquer preferências explícitas do perfil
      if (profile) {
        if (profile.favorite_categories && Array.isArray(profile.favorite_categories)) {
          userProfile.favoriteCategories = profile.favorite_categories;
          console.log(`Categorias favoritas encontradas no perfil: ${profile.favorite_categories.length}`);
        }
        
        if (profile.preferred_player_count) {
          userProfile.preferredPlayerCount = profile.preferred_player_count;
          console.log(`Número de jogadores preferido encontrado: ${profile.preferred_player_count}`);
        }
        
        if (profile.max_play_time) {
          userProfile.maxPlayTime = profile.max_play_time;
          console.log(`Tempo máximo de jogo encontrado: ${profile.max_play_time}`);
        }
        
        if (profile.complexity_preference) {
          userProfile.preferredComplexity = profile.complexity_preference;
          console.log(`Preferência de complexidade encontrada: ${profile.complexity_preference}`);
        }
      }
      
      // Se não temos coleção do usuário ou está vazia, retornar apenas as preferências explícitas
      if (!userCollection || userCollection.length === 0) {
        return userProfile;
      }
      
      console.log(`Analisando ${userCollection.length} jogos da coleção do usuário para extrair preferências`);
      
      // Jogos com avaliação boa (>= 7)
      const highRatedGames = userCollection.filter(item => 
        item.rating && item.rating >= 7 && item.game
      );
      
      // Se não temos jogos bem avaliados, tentar usar os que estão na coleção
      const gamesForAnalysis = highRatedGames.length > 0 
        ? highRatedGames 
        : userCollection.filter(item => item.game);
      
      if (gamesForAnalysis.length === 0) {
        console.log('Nenhum jogo adequado para análise de preferências');
        return userProfile;
      }
      
      console.log(`Usando ${gamesForAnalysis.length} jogos para análise de preferências`);
      
      // Contar frequência de categorias, mecânicas e temas
      const categoryCounts: Record<string, number> = {};
      const mechanicCounts: Record<string, number> = {};
      const themeCounts: Record<string, number> = {};
      
      // Somar complexidade e número de jogadores para calcular médias
      let complexitySum = 0;
      let complexityCount = 0;
      let playerCountSum = 0;
      let playerCountCount = 0;
      let playTimeSum = 0;
      let playTimeCount = 0;
      
      // Coletar estatísticas dos jogos
      gamesForAnalysis.forEach(item => {
        const game = item.game;
        if (!game) return;
        
        // Adicionar categorias
        if (game.categories && Array.isArray(game.categories)) {
          game.categories.forEach(category => {
            categoryCounts[category] = (categoryCounts[category] || 0) + 1;
          });
        }
        
        // Adicionar mecânicas
        if (game.mechanics && Array.isArray(game.mechanics)) {
          game.mechanics.forEach(mechanic => {
            mechanicCounts[mechanic] = (mechanicCounts[mechanic] || 0) + 1;
          });
        }
        
        // Adicionar temas
        if (game.themes && Array.isArray(game.themes)) {
          game.themes.forEach(theme => {
            themeCounts[theme] = (themeCounts[theme] || 0) + 1;
          });
        }
        
        // Somar complexidade para média
        if (game.complexity) {
          complexitySum += game.complexity;
          complexityCount++;
        }
        
        // Somar numero de jogadores para média
        if (game.max_players) {
          playerCountSum += game.max_players;
          playerCountCount++;
        }
        
        // Somar tempo de jogo para média
        if (game.play_time) {
          playTimeSum += game.play_time;
          playTimeCount++;
        }
      });
      
      // Extrair as categorias, mecânicas e temas mais frequentes
      userProfile.favoriteCategories = this.extractTopItems(categoryCounts, 5);
      userProfile.favoriteMechanics = this.extractTopItems(mechanicCounts, 5);
      userProfile.favoriteThemes = this.extractTopItems(themeCounts, 5);
      
      // Calcular médias para complexidade, número de jogadores e tempo
      // Só substituir os valores se não foram especificados explicitamente no perfil
      if (complexityCount > 0 && !userProfile.preferredComplexity) {
        userProfile.preferredComplexity = complexitySum / complexityCount;
      }
      
      if (playerCountCount > 0 && !userProfile.preferredPlayerCount) {
        userProfile.preferredPlayerCount = Math.round(playerCountSum / playerCountCount);
      }
      
      if (playTimeCount > 0 && !userProfile.maxPlayTime) {
        userProfile.maxPlayTime = Math.round(playTimeSum / playTimeCount);
      }
      
      console.log('Perfil extraído:', userProfile);
      
      return userProfile;
    } catch (error) {
      console.error('Erro ao extrair perfil do usuário:', error);
      return {
        favoriteCategories: [],
        favoriteMechanics: [],
        favoriteThemes: []
      };
    }
  }
  
  /**
   * Extrai os N itens mais frequentes de um objeto de contagem
   */
  private extractTopItems(countObject: Record<string, number>, limit: number): string[] {
    return Object.entries(countObject)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(item => item[0]);
  }
  
  /**
   * Atualiza o perfil do usuário com as preferências fornecidas
   */
  public async updateUserPreferences(
    userId: string,
    preferences: {
      favoriteCategories?: string[];
      preferredPlayerCount?: number;
      maxPlayTime?: number;
      preferredComplexity?: number;
    }
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          favorite_categories: preferences.favoriteCategories,
          preferred_player_count: preferences.preferredPlayerCount,
          max_play_time: preferences.maxPlayTime,
          complexity_preference: preferences.preferredComplexity,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);
      
      if (error) {
        console.error('Erro ao atualizar preferências do usuário:', error);
        return false;
      }
      
      // Invalidar cache após atualização
      this.invalidateCache(userId);
      
      console.log('Preferências do usuário atualizadas com sucesso');

      // Call gamification
      processAction({
        supabase: supabase,
        userId: userId,
        actionKey: XP_ACTIONS.UPDATE_PROFILE,
      }).catch(err => console.warn(`[Gamification Warning] Failed processAction for UPDATE_PROFILE user ${userId}:`, err));

      return true;
    } catch (error) {
      console.error('Erro ao atualizar preferências do usuário:', error);
      return false;
    }
  }

  /**
   * Obtém perfil do cache se existir e for válido
   */
  private getFromCache(userId: string): any | null {
    const cached = this.profileCache.get(userId);
    if (!cached) return null;
    
    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_TTL) {
      // Cache expirado
      this.profileCache.delete(userId);
      return null;
    }
    
    return cached.profile;
  }
  
  /**
   * Salva perfil no cache
   */
  private saveToCache(userId: string, profile: any): void {
    this.profileCache.set(userId, {
      profile,
      timestamp: Date.now()
    });
  }
  
  /**
   * Invalida cache de um usuário
   */
  private invalidateCache(userId: string): void {
    this.profileCache.delete(userId);
  }
}

// Função standalone para buscar perfil por nome de usuário
export async function getProfileByUsername(username: string): Promise<any | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*') // Seleciona todas as colunas, ajuste conforme necessário
      .eq('username', username)
      .single(); // Espera um único resultado

    if (error) {
      // Se o erro for PGROST116, significa "query returned no rows" - perfil não encontrado
      if (error.code === 'PGRST116') {
        console.log(`Perfil não encontrado para username: ${username}`);
        return null;
      }
      // Para outros erros, lança a exceção
      console.error('Erro ao buscar perfil por nome de usuário:', error);
      throw error;
    }
    return data;
  } catch (err) {
    console.error('Exceção ao buscar perfil por nome de usuário:', err);
    return null;
  }
} 